import { NgModule } from "@angular/core";

import { Routes, Router, RouterModule } from "@angular/router";
import { MsalRedirectComponent } from "@azure/msal-angular";
import { BrowserUtils } from "@azure/msal-browser";

import { HomeComponent } from "./components/home/home.component";
import { WorkRequestViewComponent } from "@app/components/work-request-view/work-request-view.component";


import { OverageComponent } from "@app/components/overage/overage.component";

import { UserProfileComponent } from "@app/components/user-profile/user-profile.component";
import { PageNotFoundComponent } from "@app/components/page-not-found/page-not-found.component";

import { BaseGuard } from "./guards/base.guard";
import { GroupGuard } from "./guards/group.guard";
import { emailAccounts, groups } from "./auth-config";


import { GlobalConstants } from "./utils/global-constants";
import { environment } from "environments/environment";
import { WorkRequestEditComponent } from "./components/work-request-view/work-request-edit.component";
import { EmailGuard } from "./guards/email.guard";
import { StatisticsComponent } from "./components/statistics/statistics.component";
import { WorkRequestStatusComponent } from "./components/work-request-view/work-request-status.component";
import { ProjectDashboardComponent } from "./components/project-dashboard/project-dashboard.component";
import { NonProjectDashboardComponent } from "./components/non-project-dashboard/non-project-dashboard.component";
import { PMGuard } from "./guards/pm.guard";
import { FeedbackComponent } from "./components/feedback/feedback.component";
import { FeedbackViewComponent } from "./components/feedback/feedback-view.component";

/**
 * MSAL Angular can protect routes in your application using MsalGuard. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#secure-the-routes-in-your-application
 */
const routes: Routes = [

    {
        path: "my-work-requests",
        component: WorkRequestViewComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: 'My IT Work Requests',
            mode: GlobalConstants.REQUEST_FORM_MODE_USER

        },
    },
    {
        path: "work-requests",
        component: WorkRequestViewComponent,
        canActivate: [GroupGuard],
        data: {
            requiredGroups: [groups.groupMember, groups.groupAdmin],
            title: 'All IT Work Requests',
            mode: GlobalConstants.REQUEST_FORM_MODE_ADMIN

        },
    },

    {
        path: "my-pm-work-requests",
        component: WorkRequestViewComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: 'My PM IT Work Requests',
            mode: GlobalConstants.REQUEST_FORM_MODE_PM
        },
    },
    {
        path: "my-sponsored-work-requests",
        component: WorkRequestViewComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: 'My Sponsored Work Requests',
            mode: GlobalConstants.REQUEST_FORM_MODE_SPONSORED
        },
    },    
    {
        path: "work-request/:id/status",
        component: WorkRequestStatusComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: 'Status Report'
        },
    },
    /*
    this is deliberatly set to no authorization because we want to use it in automation (powerautomate/)
    There is a tiny security risk here, as our URL are publickly exposed, but I don't believe the data 
    is that sensitive and guessing a proper URL would be hard.
    TODO: remove canActivate and try https://stackoverflow.com/questions/63683782/using-power-automate-i-need-to-perform-a-http-request-on-a-site-that-is-protecte
    */
    {
        path: "work-request/:id/status/:pdf",
        component: WorkRequestStatusComponent,      
        canActivate: [EmailGuard], 
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: 'Status Report'
        },
    },
    {
        path: "work-request/:id",
        component: WorkRequestEditComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "Work Request Details"
        },
    },
    {
        path: "work-request/new/:type",
        component: WorkRequestEditComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "New Work Request"
        },
    }, 
    {
        path: "feedback-view",
        component: FeedbackViewComponent,
        canActivate: [GroupGuard],
        data: {
            requiredGroups: [groups.groupMember, groups.groupAdmin],
            title: 'Work Order Feedback',
            mode: GlobalConstants.FEEDBACK_FORM_MODE_ADMIN

        },
    },       
    {
        path: "feedback/:id",
        component: FeedbackComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "Work Order Feedback",
            mode: GlobalConstants.FEEDBACK_FORM_MODE_ENTRY
        },
    },
    {
        path: "feedback/view/:id",
        component: FeedbackComponent,
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "Work Order Feedback",
            mode: GlobalConstants.FEEDBACK_FORM_MODE_VIEW
        },
    },    
    {
        path: "overage",
        component: OverageComponent,
        canActivate: [BaseGuard],
    },
    {
        // Needed for handling redirect after login
        path: "auth",
        component: MsalRedirectComponent,
    },
    {
        path: "user-profile",
        component: UserProfileComponent,
        data: {
            title: 'User Profile'
        }
    },
    { path: '', redirectTo: '/home', pathMatch: 'full' }, // redirect to `first-component`

    {
        path: "statistics",
        component: StatisticsComponent,
        canActivate: [GroupGuard],
        data: {
            requiredGroups: [ groups.groupAdmin, groups.groupReadOnly],
            title: 'Statistics'
        },
    },

    {
        path: "project-dashboard",
        component: ProjectDashboardComponent,
        
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "Project Dashboard",
            mode : GlobalConstants.REQUEST_FORM_MODE_PROJECT
        },
        
        
    }, {
        path: "restricted-project-dashboard",
        component: ProjectDashboardComponent,
        
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: "Restricted Project Dashboard",
            mode : GlobalConstants.REQUEST_FORM_MODE_RESTRICTED_PROJECT
        },
        
        
    },
    {
        path: "project-list",
        component: ProjectDashboardComponent,
        
        canActivate: [EmailGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
            title: GlobalConstants.REQUEST_FORM_TITLE_PROJECT_LIST,
            mode : GlobalConstants.REQUEST_FORM_MODE_PROJECT
        },
        
        
    },
    {
        path: "non-project-dashboard",
        component: NonProjectDashboardComponent,
        canActivate: [GroupGuard],
        data: {
            requiredGroups: [ groups.groupAdmin, groups.groupReadOnly],
            title: 'Non-Project Dashboard'
        },
    },
    {
        path: "home",
        component: HomeComponent,
        canActivate: [BaseGuard],
        data: {
            requiredEmailAccounts: emailAccounts.users,
             title: 'Enterprise Technology Hub'
        },
    },

    { path: 'pagenotfound', component: PageNotFoundComponent, 
        data: { message: 'Page not found!' ,
                title: 'Page Not Found'
        } 
    },
    { path: '**', component: PageNotFoundComponent, pathMatch: 'full', }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            // Don't perform initial navigation in iframes or popups
            initialNavigation:
                !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
                    ? "enabledNonBlocking"
                    : "disabled", // Set to enabledBlocking to use Angular Universal,
            enableTracing: true,
            useHash: environment.useHashRouting,
            onSameUrlNavigation: 'reload'


        },),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {
    constructor(
        private router: Router,
        /* Other dependencies */
    ) {

        this.router.events.subscribe(event => {
            /*
            console.group(`Router event: ${event.constructor.name}`);
            console.log(event);
            console.groupEnd();
            */
        });
    }


}


